import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { motion } from "framer-motion";
import { ChatCircleDots, Question, ChartBar, Clock } from "@phosphor-icons/react";

const RANGES = [
  { label: "7 days", value: 7 },
  { label: "30 days", value: 30 },
  { label: "90 days", value: 90 },
];

const PAGE_LABELS = {
  "/reconciliation": "Reconciliation", "/close": "Month-end Close", "/unscheduled": "Unscheduled Items",
  "/approvals": "Approvals", "/reports": "Reports", "/forecast": "Forecast", "/insights": "Insights",
  "/dashboard": "Dashboard", "/accruals": "Accruals", "/prepayments": "Prepayments",
  "/deferred-income": "Deferred Income", "/recurring-accruals": "Recurring Accruals",
  "/treatment-rules": "Treatment Rules", "/portfolio": "Clients", "/settings": "Settings",
  "/accrual-accuracy": "Accrual Accuracy", "/deferred-reports": "Revenue Recognition", "": "Other",
};
const pageLabel = (p) => PAGE_LABELS[p] || p || "Other";

const fmtDateTime = (iso) => {
  try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
};

export default function AdminAssistant() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (d) => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/assistant/questions?days=${d}`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load assistant analytics");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(days); }, [days]);

  const byPage = data?.by_page || {};
  const pageRows = Object.entries(byPage).sort((a, b) => b[1] - a[1]);
  const maxCount = pageRows.length ? pageRows[0][1] : 0;

  return (
    <AdminLayout>
      <div className="max-w-[1100px] mx-auto px-6 py-8" data-testid="admin-assistant-page">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(242,111,33,0.12)" }}>
              <ChatCircleDots size={22} weight="fill" style={{ color: "#F37A24" }} />
            </span>
            <div>
              <h1 className="font-heading font-black text-navy text-2xl tracking-tight">Assistant questions</h1>
              <p className="text-navy/70 text-sm">What users are asking "Ask Lottie" — spot where people get stuck.</p>
            </div>
          </div>
          <div className="flex items-center gap-1 bg-white rounded-xl border border-slate-200 p-1" data-testid="assistant-range">
            {RANGES.map((r) => (
              <button key={r.value} onClick={() => setDays(r.value)} data-testid={`range-${r.value}`}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${days === r.value ? "bg-navy text-white" : "text-navy/70 hover:bg-slate-100"}`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="card-surface p-12 text-center text-navy/65" data-testid="assistant-loading">Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card-surface p-4" data-testid="stat-total">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: "rgba(46,163,253,0.12)" }}>
                    <Question size={14} weight="fill" className="text-orange" />
                  </span>
                  <span className="overline text-navy/65">Questions asked</span>
                </div>
                <div className="font-heading font-black text-2xl text-navy tracking-tight">{data?.total_questions ?? 0}</div>
                <div className="text-[0.7rem] text-navy/65 mt-0.5">in the last {days} days</div>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="card-surface p-4" data-testid="stat-pages">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: "rgba(242,111,33,0.12)" }}>
                    <ChartBar size={14} weight="fill" style={{ color: "#F37A24" }} />
                  </span>
                  <span className="overline text-navy/65">Top page</span>
                </div>
                <div className="font-heading font-black text-2xl text-navy tracking-tight truncate">{pageRows.length ? pageLabel(pageRows[0][0]) : "—"}</div>
                <div className="text-[0.7rem] text-navy/65 mt-0.5">{pageRows.length ? `${pageRows[0][1]} question(s)` : "no data yet"}</div>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card-surface p-4" data-testid="stat-recent">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: "rgba(46,163,253,0.12)" }}>
                    <Clock size={14} weight="fill" className="text-orange" />
                  </span>
                  <span className="overline text-navy/65">Most recent</span>
                </div>
                <div className="font-heading font-black text-2xl text-navy tracking-tight">{data?.recent?.length ? fmtDateTime(data.recent[0].ts) : "—"}</div>
                <div className="text-[0.7rem] text-navy/65 mt-0.5">latest question</div>
              </motion.div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* By page */}
              <div className="card-surface p-5" data-testid="by-page-panel">
                <h2 className="font-heading font-bold text-navy mb-4">Questions by page</h2>
                {pageRows.length === 0 ? (
                  <p className="text-navy/65 text-sm py-6 text-center">No questions in this period yet.</p>
                ) : (
                  <div className="space-y-3">
                    {pageRows.map(([p, n]) => (
                      <div key={p} data-testid={`page-row-${p || "other"}`}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-navy/75 font-medium">{pageLabel(p)}</span>
                          <span className="text-navy/70 tabular-nums">{n}</span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${maxCount ? (n / maxCount) * 100 : 0}%`, background: "#F37A24" }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent questions */}
              <div className="card-surface p-5" data-testid="recent-panel">
                <h2 className="font-heading font-bold text-navy mb-4">Recent questions</h2>
                {(!data?.recent || data.recent.length === 0) ? (
                  <p className="text-navy/65 text-sm py-6 text-center">Nothing asked yet.</p>
                ) : (
                  <div className="space-y-3 max-h-[460px] overflow-y-auto pr-1">
                    {data.recent.map((r, i) => (
                      <div key={i} className="border-b border-slate-50 pb-3 last:border-0" data-testid={`recent-q-${i}`}>
                        <p className="text-sm text-navy/85 leading-snug">{r.content}</p>
                        <div className="flex items-center gap-2 mt-1 text-[0.68rem] text-navy/65">
                          <span>{pageLabel(r.page_route)}</span><span>·</span><span>{fmtDateTime(r.ts)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
